import { Helmet } from "react-helmet-async";

import { PageHeader } from "@/components/shared/PageHeader";
import { COMPANY, getEmailLink } from "@/config/company";

export default function TermsOfService() {
  const lastUpdated = "January 29, 2026";

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms & Conditions | {COMPANY.name}</title>
        <meta 
          name="description" 
          content={`Strict Terms and Conditions for ${COMPANY.name}. Accessing our medical tourism facilitation services constitutes a binding legal agreement.`} 
        />
        <link rel="canonical" href={`${COMPANY.website}/terms-of-service/`} />
        <meta property="og:title" content={`Terms & Conditions | ${COMPANY.name}`} />
        <meta property="og:description" content={`Strict Terms and Conditions for ${COMPANY.name}. Accessing our medical tourism facilitation services constitutes a binding legal agreement.`} />
        <meta property="og:url" content={`${COMPANY.website}/terms-of-service/`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`Terms & Conditions | ${COMPANY.name}`} />
        <meta name="twitter:description" content={`Strict Terms and Conditions for ${COMPANY.name}. Accessing our medical tourism facilitation services constitutes a binding legal agreement.`} />
        <meta name="twitter:url" content={`${COMPANY.website}/terms-of-service/`} />
      </Helmet>



      <main className="pt-24">
        <PageHeader
          title="Terms & Conditions"
          description={`Last Updated: ${lastUpdated}`}
          className="mb-10"
        />

        <div className="container mx-auto px-4 pb-20">
          <div className="max-w-4xl mx-auto prose prose-lg dark:prose-invert">
            
            {/* LEGAL WARNING ALERT */}
            <div className="bg-red-50 dark:bg-red-950/30 border-l-4 border-red-500 p-4 my-6">
              <p className="font-bold text-red-700 dark:text-red-400 m-0">BINDING LEGAL AGREEMENT</p>
              <p className="text-red-800 dark:text-red-300 text-sm mt-1 mb-0">
                THESE TERMS CONTAIN A <strong>MANDATORY ARBITRATION CLAUSE</strong> AND A <strong>CLASS ACTION WAIVER</strong>. 
                BY USING THIS SITE, YOU WAIVE YOUR RIGHT TO SUE IN COURT OR PARTICIPATE IN A CLASS ACTION LAWSUIT. 
                READ CAREFULLY.
              </p>
            </div>

            <p className="lead">
              Welcome to <strong>{COMPANY.name}</strong> ("Company", "we", "us"), doing business as TrustedHeal India. 
              These Terms & Conditions ("Terms") constitute a legally binding agreement between you ("User", "Patient") and the Company regarding your access to and use of our website and services.
            </p>

            <h2>1. NO DOCTOR-PATIENT RELATIONSHIP</h2>
            <p>
              <strong>THE COMPANY IS A FACILITATOR, NOT A MEDICAL PROVIDER.</strong>
            </p>
            <p>
              We are a technology platform connecting patients with independent third-party hospitals and doctors. 
              <strong>NO DOCTOR-PATIENT RELATIONSHIP IS CREATED</strong> between you and the Company at any time. 
              All medical advice, diagnoses, treatments, and procedures are provided solely by the independent medical professionals you select.
            </p>
            <p>
              <strong>EMERGENCY WARNING:</strong> IF YOU HAVE A MEDICAL EMERGENCY, DO NOT USE THIS PLATFORM. CALL YOUR LOCAL EMERGENCY SERVICES IMMEDIATELY.
            </p>

            <h2>2. AI & Technology Disclaimer</h2>
            <div className="bg-yellow-50 dark:bg-yellow-950/30 border-l-4 border-yellow-500 p-4 my-4">
              <p className="font-bold text-yellow-700 dark:text-yellow-400 m-0">AI HALLUCINATION WARNING</p>
              <p className="text-yellow-800 dark:text-yellow-300 text-sm mt-1 mb-0">
                Our Platform utilizes Artificial Intelligence (AI) to provide information, estimates, and conversational assistance. 
                <strong>AI MAY PRODUCE INACCURATE OR HALLUCINATED INFORMATION.</strong> 
                You agree that you will not rely solely on AI-generated responses for medical or financial decisions. 
                ALWAYS verify critical information with a human case manager or the hospital directly.
              </p>
            </div>

            <h2>3. Strict Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL {COMPANY.name}, ITS AFFILIATES, OFFICERS, EMPLOYEES, OR AGENTS BE LIABLE FOR:
            </p>
            <ul>
              <li>Any medical negligence, malpractice, misdiagnosis, or treatment failure by third-party hospitals/doctors.</li>
              <li>Any complications, infections, or death resulting from medical procedures.</li>
              <li>Travel delays, visa rejections, or accommodation issues.</li>
              <li>Any indirect, punitive, special, incidental, or consequential damages.</li>
            </ul>
            <p>
              <strong>CAP ON LIABILITY:</strong> OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR OUR SERVICES 
              IS STRICTLY LIMITED TO THE AMOUNT YOU PAID DIRECTLY TO US (IF ANY) IN THE LAST SIX (6) MONTHS, OR <strong>$100 USD</strong>, WHICHEVER IS GREATER.
            </p>
            <p>
              <strong>EXCEPTIONS:</strong> The above limitation shall <strong>not</strong> apply to liability arising from (i) our gross negligence or willful misconduct, or (ii) statutory damages payable under the Digital Personal Data Protection Act, 2023.
            </p>

            <h2>4. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless {COMPANY.name} and its affiliates from any claims, liabilities, damages, and expenses (including legal fees) 
              arising from your violation of these Terms, your medical treatment, or your violation of any third-party rights.
            </p>

            <h2>5. Payment & Financial Terms</h2>
            <ul>
              <li><strong>Direct Hospital Payments:</strong> We generally advise patients to pay hospitals directly. The Company is not responsible for refund disputes with hospitals.</li>
              <li><strong>Facilitation Fees:</strong> Any fees paid to us for concierge services are non-refundable once the service has commenced.</li>
              <li><strong>Estimate Only:</strong> Cost estimates provided by our AI or team are provisional. Final billing is determined by the hospital based on actual treatment required.</li>
            </ul>

            <h2>6. Arbitration & Governing Law</h2>
            <p>
              <strong>Dispute Resolution:</strong> Any dispute, controversy, or claim arising out of or relating to these Terms shall be settled by binding arbitration.
            </p>
            <ul>
              <li><strong>Jurisdiction:</strong> Use of this site is governed by the laws of <strong>India</strong>.</li>
              <li><strong>Venue:</strong> The exclusive venue for any arbitration or legal proceeding shall be <strong>Gurugram, Haryana, India</strong>.</li>
              <li><strong>Class Action Waiver:</strong> You agree to resolve disputes only on an individual basis and strictly waive any right to bring a class action lawsuit.</li>
              <li><strong>Regulatory Carve-out:</strong> Nothing in this section shall prevent you from filing a complaint with the Data Protection Board of India or applicable regulatory bodies for statutory violations.</li>
            </ul>

            <h2>7. Force Majeure</h2>
            <p>
              We shall not be liable for any failure to perform our obligations where such failure results from any cause beyond our reasonable control, 
              including but not limited to mechanical, electronic, or communications failure, pandemics, acts of God, or government travel restrictions.
            </p>


            <h2>8. User Conduct & Prohibited Acts</h2>
            <p>
              You agree specifically NOT to:
            </p>
            <ul>
              <li>Use the Platform for any illegal purpose or in violation of any local, state, national, or international law.</li>
              <li>Harass, threaten, or abuse our employees, agents, or other users.</li>
              <li>Attempt to scrape, data-mine, or reverse-engineer the Platform or its AI models.</li>
              <li>Submit false or misleading medical information.</li>
            </ul>

            <h2>9. Termination of Service</h2>
            <p>
              We reserve the absolute right to suspend or terminate your access to the Platform immediately, without prior notice or liability, 
              for any reason, including but not limited to a breach of these Terms. 
              Upon termination, your data will be retained or deleted in accordance with our Privacy Policy and applicable medical record retention laws.
            </p>

            <h2>10. General Provisions</h2>
            <ul>
              <li><strong>Severability:</strong> If any provision of these Terms is held to be unenforceable, that provision shall be modified to the minimum extent necessary to make it enforceable, and the remaining provisions shall remain in full force and effect.</li>
              <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and {COMPANY.name} regarding the subject matter and supersede all prior communications.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.</li>
              <li><strong>Assignment:</strong> You may not assign your rights under these Terms. We may assign our rights and obligations to any third party without restriction.</li>
            </ul>

            <h2>11. Contact Legal Team</h2>
            <p>
              Notices to the Company regarding legal matters must be sent in writing to:
            </p>
            <p className="bg-muted p-6 rounded-lg border">
              <strong>Legal Department</strong><br />
              {COMPANY.name}<br />
              <strong>Address:</strong> {COMPANY.address.multiline}<br />
              <strong>Email:</strong> <a href={getEmailLink("support")} className="text-primary hover:underline">{COMPANY.email.support}</a>
            </p>
            
          </div>
        </div>
      </main>


    </div>
  );
}
